/**
 * Sprint 1070.C5-prep5 — `<WebStudioShellWrap>`.
 *
 * Drop-in wrapper that auto-applies the Web Studio `<StudioShell>` (sidebar
 * + dual-breadcrumb header + Credits pill) when the URL carries
 * `?source=web-studio`. Lets canonical pages like `/platform/external-site/*`
 * stay "inside" Web Studio when the tenant deeplinks from the sidebar — and
 * remain standalone when accessed directly.
 *
 * Props
 *   activeSection : sidebar group key currently active (e.g. "integrate",
 *                   "widgets", "create"). Highlights the matching group.
 *   activeItemKey : sub-item key currently active (e.g. "integrate-hub",
 *                   "widgets-catalog"). Highlights the matching nav row.
 *   children      : page content
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import StudioShell from "./StudioShell";
import {
  WEB_STUDIO_IDENTITY,
  SECTION_TO_STAGE,
  buildWebStudioSidebar,
  buildMissionStages,
} from "../../config/webStudioShell";

export default function WebStudioShellWrap({ activeSection, activeItemKey, children }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fromWebStudio = searchParams.get("source") === "web-studio";

  const [state, setState] = useState(null);
  useEffect(() => {
    if (!fromWebStudio) return;
    let cancelled = false;
    api.get("/web-studio/state")
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch(() => { /* graceful — shell still renders without counts */ });
    return () => { cancelled = true; };
  }, [fromWebStudio]);

  const sidebarGroups = useMemo(() => buildWebStudioSidebar(), []);
  const missionStages = useMemo(() => buildMissionStages(state, t), [state, t]);

  if (!fromWebStudio) return children;

  return (
    <StudioShell
      studio={WEB_STUDIO_IDENTITY}
      sidebarGroups={sidebarGroups}
      activeSection={activeSection}
      activeItemKey={activeItemKey}
      sectionToStage={SECTION_TO_STAGE}
      missionStages={missionStages}
      onSectionPick={(_sectionKey, item) => navigate(item.href)}
    >
      {children}
    </StudioShell>
  );
}
